const _ = require("lodash");
const EventEmitter = require("events");
const ErrorUtil = require("../../../../shared/ErrorUtil");

/**
 * Was getting "Declaration emit for this file requires using private name".
 * This `type` annotation is a workaround. See: https://stackoverflow.com/questions/60009092/declaration-will-not-emit-due-to-private-name-usage
 * @type {unknown}
 */
const Vault = function(updater) {
	if (!(this instanceof Vault)) {
		return new Vault(updater);
	}
	this.init = false;
	this.state = {};
	this.stateGeneration = 1;
	this.pendingEvents = [];
	this.lastEvents = [];
	this.updater = updater;
	this.emitter = new EventEmitter();
	this.emitter.setMaxListeners(200);
	this.updatePlugins = {};

	this.dispatchQueue = [];
	this.isPreparingDispatch = false;
	this.isHandlingAction = false;
	this.isEmitting = false;

	this.dispatchMiddleware = [];
	this.preHandleMiddleware = [];
	this.postHandleMiddleware = [];
	this.updateErrorMiddleware = [];
};

// Private helpers

const updateWithAction = function(vault, action) {
	const oldState = vault.state;
	action = processPreHandleMiddleware(vault, action);
	if (!action) {
		return;
	}
	vault.state = vault.updater.update(vault.getUpdaterInterface(), action);
	if (!vault.state) {
		throw new Error("The vault update function has to return a value");
	}
	processPostHandleMiddleware(vault, action, oldState);
};

const emitUpdateEvent = function(vault) {
	vault.stateGeneration += 1;
	vault.lastEvents = vault.pendingEvents;
	vault.pendingEvents = [];
	vault.isEmitting = true;
	try {
		vault.emitter.emit(
			"update",
			vault.state,
			vault.stateGeneration,
			vault.lastEvents
		);
	} finally {
		vault.isEmitting = false;
	}
};

const processActions = function(vault, actions) {
	const oldState = vault.state;
	vault.isHandlingAction = true;
	let currAction;
	try {
		actions.forEach(a => {
			currAction = a;
			updateWithAction(vault, a);
		});
		vault.isHandlingAction = false;
	} finally {
		if (vault.isHandlingAction) {
			// We only reach this branch if an exception was thrown. We don't use catch
			// because rethrowing the exception changes the stack trace.
			processUpdateErrorMiddleware(vault, currAction);
		}
		vault.isHandlingAction = false;
	}
	if (vault.state !== oldState || vault.pendingEvents.length) {
		emitUpdateEvent(vault);
	}
};

const processDispatchQueue = function(vault) {
	if (!vault.init) {
		// This function gets called again when init happens.
		return;
	}

	// Ensure dispatches that occur before this finishes will be processed next timeout.
	vault.isPreparingDispatch = false;
	const queue = vault.dispatchQueue;
	vault.dispatchQueue = [];

	if (queue.length) {
		processActions(vault, queue);
	}
};

Vault.prototype.getState = function() {
	return this.state;
};

Vault.prototype.getGeneration = function() {
	return this.stateGeneration;
};

Vault.prototype.getLastCustomEvents = function() {
	return this.lastEvents;
};

Vault.prototype.on = function(name, listener) {
	this.emitter.addListener(name, listener);
};

Vault.prototype.off = function(name, listener) {
	this.emitter.removeListener(name, listener);
};

// Available options:
// - init: If true, this is the initialization action. No actions should be
//   handled before one of these is.
// - suppressErrors: Boolean, default false. Some actions, like those in
//   onbeforeunload, can circumvent the usual flow of JS execution. They should
//   skip the checks in this function. Set suppressErrors to true for them.
Vault.prototype.dispatchNow = function(action, options) {
	const {init = false, suppressErrors = false} = options || {};

	if (!init && !this.init) {
		// Init action hasn't been dispatched. Queue this one instead.
		return this.dispatch(action);
	} else if (this.isHandlingAction) {
		if (!suppressErrors) {
			ErrorUtil.log(
				"E_VAULT_DISPATCH",
				"dispatchNow'd an action " +
					action.type +
					" while handling another action."
			);
		}
		return this.dispatch(action);
	} else if (this.isEmitting) {
		if (!suppressErrors) {
			ErrorUtil.log(
				"E_VAULT_DISPATCH",
				"dispatchNow'd an action " + action.type + " during vault events."
			);
		}
		return this.dispatch(action);
	}
	action = processDispatchMiddleware(this, action);
	if (!action) {
		return;
	}
	processActions(this, [action]);

	if (!this.init && init) {
		this.init = true;
		processDispatchQueue(this);
	}
};

Vault.prototype.dispatch = function(action) {
	action = processDispatchMiddleware(this, action);
	if (!action) {
		return;
	}
	this.dispatchQueue.push(action);
	if (!this.isPreparingDispatch) {
		this.isPreparingDispatch = true;
		setTimeout(() => processDispatchQueue(this), 0);
	}
};

Vault.prototype.emitCustomEventFromUpdate = function(name, ...args) {
	this.pendingEvents.push({name, args});
};

// Can also pass an object mapping plugin names to plugins.
Vault.prototype.addUpdatePlugin = function(name, value) {
	if (name && typeof name === "object") {
		_.extend(this.updatePlugins, name);
	} else {
		this.updatePlugins[name] = value;
	}
};

Vault.prototype.getViewInterface = function() {
	return {
		getState: _.bind(this.getState, this),
		getGeneration: _.bind(this.getGeneration, this),
		getLastCustomEvents: _.bind(this.getLastCustomEvents, this),
		dispatch: _.bind(this.dispatch, this),
		on: _.bind(this.on, this),
		off: _.bind(this.off, this),
	};
};

Vault.prototype.getUpdaterInterface = function() {
	return _.extend(
		{
			state: this.state,
			dispatch: _.bind(this.dispatch, this),
			emitEvent: _.bind(this.emitCustomEventFromUpdate, this),
		},
		this.updatePlugins
	);
};

// Middleware methods. Middleware is a function that gets called when certain
// vault things happen and can optionally modify the behavior. If multiple
// middleware functions of the same type are added, they are called in the
// order they were added.

// In general, middleware returning undefined means don't change anything.
// Middleware returning any other falsy value means to cancel the operation.
// Arguments passed to middleware should not be directly mutated.

// Called right when an action is dispatched. It is passed (action, state) and
// can optionally return a modified action to dispatch instead.
Vault.prototype.addDispatchMiddleware = function(f) {
	this.dispatchMiddleware.push(f);
};

const processDispatchMiddleware = function(vault, action) {
	vault.dispatchMiddleware.forEach(f => {
		const result = action && f(action, vault.state);
		action = result === undefined ? action : result;
	});
	return action;
};

// Called right before an action is handled in the vault updater. It is passed
// (action, state) and can optionally return a modified action to dispatch
// instead.
Vault.prototype.addPreHandleMiddleware = function(f) {
	this.preHandleMiddleware.push(f);
};

const processPreHandleMiddleware = function(vault, action) {
	vault.preHandleMiddleware.forEach(f => {
		const result = action && f(action, vault.state);
		action = result === undefined ? action : result;
	});
	return action;
};

// Called right after an action is handled in the vault updater. It is passed
// (action, oldState, newState). The return is ignored.
Vault.prototype.addPostHandleMiddleware = function(f) {
	this.postHandleMiddleware.push(f);
};

const processPostHandleMiddleware = function(vault, action, oldState) {
	vault.postHandleMiddleware.forEach(f => {
		f(action, oldState, vault.state);
	});
};

// Called if something threw an exception in the process of handling an action.
// It is passed (action, state). The return is ignored.
Vault.prototype.addUpdateErrorMiddleware = function(f) {
	this.updateErrorMiddleware.push(f);
};

const processUpdateErrorMiddleware = function(vault, action) {
	vault.updateErrorMiddleware.forEach(f => {
		const result = action && f(action, vault.state);
		action = result === undefined ? action : result;
	});
	return action;
};

module.exports = Vault;
